import { Col, Row, Alert, Affix, Modal, Input, Button, Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import styled from 'styled-components';
import { DownOutlined } from '@ant-design/icons';

import Loader from '../../UI/Loader';
import useLocale from '../../hooks/useLocale';
import {
  getJob,
  validateJobInviteToken,
} from '../../api/jobs';
import RoundAddButton from '../../UI/buttons/RoundAddButton';
import CandidateChooseModal from '../../components/candidates/CandidateChooseModal';
import CandidateIntroduceModal from '../../components/candidates/CandidateIntroduceModal';
import CandidateAddForm from '../../components/form/CandidateAddForm';
import ComingSoon from '../../components/helper/ComingSoon';
import ActivitySection from '../../components/jobs/singleJob/ActivitySection';
import ApplicationProcess from '../../components/jobs/singleJob/ApplicationProcess';
import Banner from '../../components/jobs/singleJob/Banner';
import BenefitSection from '../../components/jobs/singleJob/BenefitSection';
import CareerSection from '../../components/jobs/singleJob/CareerSection';
import ColleagueSection from '../../components/jobs/singleJob/ColleagueSection';
import CompanyFactSection from '../../components/jobs/singleJob/CompanyFactSection';
import DescriptionSection from '../../components/jobs/singleJob/DescriptionSection';
import RecruiterFeeSection from '../../components/jobs/singleJob/RecruiterFeeSection';
import SectionItem from '../../components/jobs/singleJob/SectionItem';
import SimilarJobSection from '../../components/jobs/singleJob/SimilarJobSection';
import SkillSection from '../../components/jobs/singleJob/SkillSection';
import TeamSection from '../../components/jobs/singleJob/TeamSection';
import ToolSection from '../../components/jobs/singleJob/ToolSection';
import ExtensionFieldsSection from '../../components/jobs/singleJob/ExtensionFieldsSection';
import { CLIENT_ROLE, RECRUITER_ROLE } from '../../constants/roles';
import useCurrency from '../../hooks/useCurrency';
import useEmploymentTypes from '../../hooks/useEmploymentTypes';
import useHirePortTheme from '../../hooks/useHirePortTheme';
import { ReactComponent as CaseIcon } from '../../icons/case.svg';
import { ReactComponent as HomeIcon } from '../../icons/home.svg';
import { ReactComponent as QuoteIcon } from '../../icons/quote.svg';
import getEmploymentTypeSummary from '../../utils/getEmploymentTypeSummary';
import useSingleJob from './hooks';
import { useInviteModal } from './hooks';
import {
  ApplicationProcessWrap,
  InterviewDescription,
  SingleJobBody,
} from './styles';

type SingleJobProps = {
  isPublic: boolean;
  showBackButton?: boolean;
}

// Styled-components for invite modal
const StyledInviteModal = styled(Modal)`
  & .ant-modal-content {
    padding: 32px;
    border-radius: ${({ theme }) => theme.radius};
    background: #fff;
  }
  & .ant-modal-header {
    border-bottom: none;
    padding: 0;
    margin-bottom: 16px;
    & div {
      font-size: 20px;
      font-weight: bold;
      line-height: 1.2;
      color: #061c2e;
    }
  }
  & .ant-modal-body {
    padding: 0;
  }
  & .ant-modal-footer {
    padding: 0;
    display: flex;
    border-top: none;
  }
`;
const StyledInviteButton = styled(Button)`
  &.ant-btn-primary {
    background-color: ${({ theme }) => theme.primaryColor};
    border-color: ${({ theme }) => theme.primaryColor};
    border-radius: ${({ theme }) => theme.radius};
    font-weight: 600;
    height: 40px;
  }
`;
const StyledInviteInput = styled(Input)`
  border-radius: ${({ theme }) => theme.radius};
  margin-bottom: 8px;
  height: 40px;
  box-shadow: none !important;
  &.ant-input:hover {
    border: solid 1px ${({ theme }) => theme.hoverColor};
  }
  &:focus, &.ant-input-focused {
    border-color: ${({ theme }) => theme.primaryColor} !important;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.primaryColor}22 !important;
  }
`;
const InviteModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

// Styled email input group for modal
const EmailInputGroup = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  width: 100%;
`;
const EmailPrefixInput = styled(Input)`
  flex: 1;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-right: none !important;
  height: 40px;
  font-size: 16px;
  border-radius: 0.5rem 0 0 0.5rem !important;
  box-shadow: none !important;
  &:focus, &.ant-input-focused {
    border-color: ${({ theme }) => theme.primaryColor} !important;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.primaryColor}22 !important;
  }
  &.ant-input:hover {
    border: solid 1px ${({ theme }) => theme.hoverColor};
  }
`;
const EmailDomainSelect = styled(Select)`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  .ant-select-selector {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-top-right-radius: 0.5rem !important;
    border-bottom-right-radius: 0.5rem !important;
    height: 40px !important;
    display: flex;
    align-items: center;
    font-size: 16px;
    border-left: 1px solid #e0e0e0 !important;
    box-shadow: none !important;
    padding-right: 32px !important;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  .ant-select-selector:focus, &.ant-select-focused .ant-select-selector {
    border-color: ${({ theme }) => theme.primaryColor} !important;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.primaryColor}22 !important;
  }
  &.ant-select-focused .ant-select-selector,
  .ant-select-selector:hover {
    border-color: ${({ theme }) => theme.hoverColor} !important;
  }
  .ant-select-selection-item {
    display: flex;
    align-items: center;
    font-size: 16px;
  }
`;
const EmailDomainSuffix = styled.span`
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-left: 1px solid #e0e0e0;
  border-top-right-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  font-size: 16px;
  color: #627d95;
  flex: 1;
  min-width: 0;
`;

// Utility to decode JWT and extract recruiter_domains
function getDomainsFromToken(token: string | null): string[] {
  if (!token) return [];
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.recruiter_domains || (payload.recruiter_domain ? [payload.recruiter_domain] : []);
  } catch {
    return [];
  }
}

const SingleJob = ({ isPublic, showBackButton = true }: SingleJobProps) => {
  const [t] = useTranslation();
  const theme = useHirePortTheme();
  const location = useLocation();
  const { setSessionLocale } = useLocale();
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteEmailPrefix, setInviteEmailPrefix] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [domainValidationEnabled, setDomainValidationEnabled] = useState<boolean>(true);

  const {
    actions: {
      trackSection,
      addCandidateItem,
      editCandidate,
      setIntroducedList,
      setAddCandidate,
      setStartIntroduction,
      setJob,
      setSelectedCandidate,
    },
    data: {
      job,
      data: supplementalJobData,
      introducedList,
      addCandidate,
      addedCandidate,
      startIntroduction,
      languages,
      activityFeed,
      loading,
      roles,
      user,
      selectedCandidate,
      tree,
      id,
      editModal,
      isRecruiter,
    },
  } = useSingleJob(isPublic);
  const currency = useCurrency(job?.compensation_currency);
  const isClient = user?.role === CLIENT_ROLE;

  const [favorite, setFavorite] = useState<boolean>(false);
  const employmentTypes = useEmploymentTypes();
  const { isAuth } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();

  // Invite modal hook
  const inviteModal = useInviteModal({
    location,
    id,
    isAuth,
    startIntroduction,
    dispatch,
    setStartIntroduction,
    domainValidationEnabled,
    setDomainValidationEnabled,
  });

  useEffect(() => {
    setFavorite(!!job?.is_favorite);
  }, [job?.is_favorite]);

  useEffect(() => {
    // Extract invite_token from URL
    const params = new URLSearchParams(location.search);
    const token = params.get('invite_token');
    console.log('🔍 Invite token from URL:', token);
    if (token) {
      setInviteToken(token);

      // Fetch domain validation setting from API
      const fetchDomainValidation = async () => {
        try {
          console.log('🔍 Fetching domain validation from API for job ID:', id);
          const response = await validateJobInviteToken(id, token);
          const enabled = response.data.domain_validation_enabled;
          const language = response.data.language;
          console.log('🔍 API response - domain validation enabled:', enabled, 'language:', language);
          setDomainValidationEnabled(enabled);

          // Set the language from the recruiter company
          if (language) {
            console.log('🔍 Setting language to:', language);
            setSessionLocale(language);
          }
        } catch (error) {
          console.error('🔍 Failed to fetch domain validation setting:', error);
          // Default to true for security
          setDomainValidationEnabled(true);
        }
      };

      fetchDomainValidation();
    }
  }, [location.search, id, setSessionLocale]);

  useEffect(() => {
    // Automatically open candidate introduction if ?introduce=1 is present
    const params = new URLSearchParams(location.search);
    if (params.get('introduce') === '1') {
      setStartIntroduction(true);
    }
  }, [location.search, setStartIntroduction]);

  useEffect(() => {
    // Clear email prefix when domain validation is disabled
    if (!domainValidationEnabled) {
      setInviteEmailPrefix('');
      setSelectedDomain('');
    }
  }, [domainValidationEnabled]);

  // Extract domains from invite token only if domain validation is enabled
  const inviteDomains = domainValidationEnabled ? getDomainsFromToken(inviteToken) : [];
  const inviteDomain = domainValidationEnabled ? (inviteDomains.length === 1 ? inviteDomains[0] : selectedDomain) : '';

  const tollsGraph = useMemo(() => {
    if (!job || !Array.isArray(job.tools_used) || !job.tools_used.length) {
      return null;
    }
    return <ToolSection data={job.tools_used} />;
  }, [job]);

  const teamGraph = useMemo(() => {
    if (!job?.team_roles || !Array.isArray(job.team_roles) || !job.team_roles.length) {
      return null;
    }
    return (
      <TeamSection
        roles={roles}
        team={job.team_roles}
        languages={job?.spoken_languages ? job.spoken_languages : []}
        languageList={languages}
      />
    );
  }, [roles, job, languages]);

  const { softSkills, industrySkills, minEducation, preferableEducation } =
    useMemo(() => {
      let softSkills: string[] = [];
      let industrySkills: string[] = [];
      let minEducation: { id: string; name: string }[] =
        job?.min_education || [];
      let preferableEducation: { id: string; name: string }[] =
        job?.preferable_education || [];

      if (job?.soft_skills && job.soft_skills.length) {
        softSkills = job?.soft_skills.split(',');
      }

      if (job?.industry_experiences && job.industry_experiences.length) {
        industrySkills = job?.industry_experiences.split(',');
      }

      if (job?.min_education_custom && job.min_education_custom.length) {
        minEducation = [
          ...minEducation,
          { id: job.min_education_custom, name: job.min_education_custom },
        ];
      }

      if (job?.preferable_education_custom && job.preferable_education_custom.length) {
        preferableEducation = [
          ...preferableEducation,
          {
            id: job?.preferable_education_custom,
            name: job?.preferable_education_custom,
          },
        ];
      }

      return {
        softSkills,
        industrySkills,
        minEducation,
        preferableEducation,
      };
    }, [job]);

  const employmentTypeSummary =
    job && getEmploymentTypeSummary(t, currency, job?.employment_type, employmentTypes);
  const additionalWorkingConditions = (
    employmentTypeSummary?.fields['working_conditions'] || []
  ).map(
    (workingCondition) =>
      `${workingCondition.label}: ${workingCondition.value || ''}`,
  );

  // Only use 1/3 layout for team / activities and tool if all three are defined
  // Fallback to 1/2 so there is no whole in the grid
  const useThirdColumnLayout = job?.team_members_num
    && Array.isArray(job?.activities) && job.activities.length
    && Array.isArray(job?.tools_used) && job.tools_used.length;

  // Full width job description block if no matching criteria is defined
  const hasMatchingCriteriaColumn =
    job && (job.key_skills.length > 0 || job.preferable_skills.length > 0);

  return (
    <div style={{ backgroundColor: theme.lozengeColor }}>
      {isRecruiter && job?.has_action_required && (
        <Affix offsetTop={0}>
          <div
            style={{
              padding: '16px 20px',
              backgroundColor: '#FFFFFF',
              borderBottom: '1px solid #E8E8E8',
              textAlign: 'center',
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <span style={{ marginRight: '16px', color: '#D48806', fontWeight: 'bold' }}>{t('YOU_HAVE_UNANSWERED_QUESTIONS')}</span>
            <button
              onClick={() => {
                document.getElementById('extension_fields')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center'
                });
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#FA8C16',
                color: 'white',
                fontWeight: 'bold',
                borderRadius: '4px',
                display: 'inline-block',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              {t('SCROLL_TO_QUESTIONS')}
            </button>
          </div>
        </Affix>
      )}
      <Loader spinning={loading}>
        <Banner
          role={user?.role}
          languageList={languages}
          onAction={setJob}
          handleIntroduction={setStartIntroduction}
          loading={loading}
          languages={job?.languages ? job?.languages : []}
          activityFeed={activityFeed}
          job={job}
          onSetFavorite={setFavorite}
          favorite={favorite}
          showBackButton={showBackButton}
          inviteToken={inviteToken}
        />
        <SingleJobBody>
          <Row gutter={[16, 16]}>
            {(isRecruiter || isClient) && job && (
              <Col xs={{ span: 24 }} md={{ span: 24 }} xl={{ span: 24 }}>
                {!user?.permissions || Object.keys(user.permissions).length === 0 || user.permissions.view_feeinfo ? (
                  <SectionItem
                    title={t('RECRUITER_JOB_DETAILS')}

                    name="fee_details"
                    jobFeedbacks={job?.job_feedbacks}
                    trackSection={trackSection}
                    routeToEdit={isClient && `/edit/${job.id}/fees`}
                  >
                    <RecruiterFeeSection job={job} isClient={isClient} />
                  </SectionItem>
                ) : null}
              </Col>
            )}
            {job?.company?.description &&
              <Col xs={{ span: 24 }} md={{ span: 24 }} xl={{ span: 12 }}>
                <SectionItem
                  title={t('ABOUT_COMPANY')}
                  name="about_company"
                  jobFeedbacks={job?.job_feedbacks}
                  trackSection={trackSection}
                  routeToEdit={isClient && `/edit/${job?.id}/company_info`}
                >
                  <DescriptionSection text={job.company.description}>
                    {job?.company?.difference_description && (
                      <SectionItem title={t('COMPANY_USPS')}>
                        <p>{job?.company?.difference_description}</p>
                      </SectionItem>
                    )}
                  </DescriptionSection>
                </SectionItem>
              </Col>
            }
            <Col xs={{ span: 24 }} md={{ span: 24 }} xl={{ span: hasMatchingCriteriaColumn ? 12 : 24 }}>
              <SectionItem
                title={t('JOB_DESCRIPTION')}
                name="job_description"
                jobFeedbacks={job?.job_feedbacks}
                trackSection={trackSection}
                routeToEdit={isClient && `/edit/${job?.id}/info`}
              >
                <DescriptionSection text={job?.description || ''}>
                  {!job?.description && (
                    <ComingSoon
                      onSetFavorite={setFavorite}
                      favorite={favorite}
                      job={job}
                    />
                  )}
                  {job?.current_projects && (
                    <SectionItem title={t('CURRENT_PROJECTS')}>
                      <p>{job?.current_projects}</p>
                    </SectionItem>
                  )}
                  {job?.ideal_candidate && (
                    <SectionItem title={t('IDEAL_CANDIDATE')}>
                      <p>{job?.ideal_candidate}</p>
                    </SectionItem>
                  )}
                  {job?.problems_description && (
                    <SectionItem title={t('CANDIDATE_SOLVES_PROBLEM')}>
                      <p>{job?.problems_description}</p>
                    </SectionItem>
                  )}
                </DescriptionSection>
              </SectionItem>
            </Col>
            {job?.extension_fields && job.extension_fields.length > 0 && (
              <Col xs={{ span: 24 }} md={{ span: 24 }} xl={{ span: hasMatchingCriteriaColumn ? 12 : 24 }}>
                <SectionItem
                  title={t('ADDITIONAL_INFORMATION')}
                  name="extension_fields"
                  jobFeedbacks={job?.job_feedbacks}
                  trackSection={trackSection}
                  routeToEdit={isClient && `/edit/${job.id}/extension`}
                >
                  {isRecruiter && job?.has_action_required && (
                    <div style={{ marginBottom: '16px' }}>
                      <Alert
                        message={t('PENDING_QUESTIONS')}
                        type="warning"
                        showIcon
                        style={{
                          borderRadius: '4px',
                          backgroundColor: '#FFF7E6',
                          borderColor: '#FFD591'
                        }}
                      />
                    </div>
                  )}
                  <ExtensionFieldsSection
                    fields={job.extension_fields}
                    jobId={job.id}
                    onUpdate={() => {
                      getJob(job.id).then(res => setJob(res.data));
                    }}
                    permissions={{
                      canAnswer: job.extension_fields_permissions?.can_answer || false,
                      canEdit: job.extension_fields_permissions?.can_edit || false
                    }}
                  />
                </SectionItem>
              </Col>
            )}
            {hasMatchingCriteriaColumn &&
              <Col xs={{ span: 24 }} md={{ span: 12 }} xl={{ span: 12 }}>
                <SectionItem
                  title={t('MATCHING')}
                  name="skills"
                  jobFeedbacks={job?.job_feedbacks}
                  trackSection={trackSection}
                  routeToEdit={isClient && `/edit/${job?.id}/profile`}
                >
                  {!!job?.key_skills.length &&
                    <SkillSection
                      title="Must"
                      skills={job?.key_skills || []}
                      education={minEducation}
                      certificates={job?.min_certificates}
                      languages={job?.languages}
                      languageList={languages}
                    />
                  }

                  {!!job?.preferable_skills.length &&
                    <SkillSection
                      title={t('PREFERABLE')}
                      skills={job?.preferable_skills || []}
                      education={preferableEducation}
                      certificates={job?.preferable_certificates}
                      languages={
                        job?.preferable_language ? [...job?.preferable_language] : []
                      }
                      light={true}
                      languageList={languages}
                    />
                  }

                  {!!softSkills.length && (
                    <SkillSection
                      title={t('SOFT_SKILLS')}
                      skills={[]}
                      education={[]}
                      certificates={[]}
                      languages={[]}
                      languageList={{}}
                      softSkills={softSkills}
                    />
                  )}

                  {!!industrySkills.length && (
                    <SkillSection
                      title={t('INDUSTRY_EXP')}
                      skills={[]}
                      education={[]}
                      certificates={[]}
                      languages={[]}
                      languageList={{}}
                      industrySkills={industrySkills}
                    />
                  )}
                </SectionItem>
              </Col>
            }
            <Col xs={{ span: 24 }} md={{ span: 12 }} xl={{ span: 12 }}>
              <SectionItem
                title={t('Compensation & benefits')}
                name="what_you_get"
                jobFeedbacks={job?.job_feedbacks}
                trackSection={trackSection}
                bg_image={<CaseIcon className="section-bg" />}
                routeToEdit={isClient && `/edit/${job?.id}/offer`}
              >
                <BenefitSection job={job || undefined} />
              </SectionItem>
            </Col>
            <Col xs={{ span: 24 }} md={{ span: 24 }} xl={{ span: 12 }}>
              <SectionItem
                title={t('WORKING_CONDITIONS')}
                name="company_facts"
                jobFeedbacks={job?.job_feedbacks}
                trackSection={trackSection}
                bg_image={<HomeIcon className="section-bg" />}
                routeToEdit={isClient && `/edit/${job?.id}/profile`}
              >
                <CompanyFactSection
                  visa={job?.is_visa_available}
                  daysAtTheOffice={job?.days_at_the_office}
                  daysWorkingFromHome={job?.days_working_from_home}
                  holidays={job?.holidays_number}
                  relocationPackage={job?.relocation_package}
                  relocation={job?.relocation}
                  workingType={job?.working_type}
                  additional={additionalWorkingConditions}
                />
              </SectionItem>
            </Col>
            {job?.company.company_culture &&
              <Col xs={{ span: 24 }} md={{ span: 24 }} xl={{ span: 12 }}>
                <SectionItem
                  title={t('COMPANY_CULTURE_LABEL')}
                  name="company_culture"
                  jobFeedbacks={job?.job_feedbacks}
                  trackSection={trackSection}
                  routeToEdit={isClient && `/edit/${job?.id}/company_info`}
                >
                  <DescriptionSection text={job.company.company_culture} />
                </SectionItem>
              </Col>
            }
            {!!job?.team_members_num &&
              <Col xs={{ span: 24 }} md={{ span: 24 }} xl={{ span: useThirdColumnLayout ? 8 : 12 }}>
                <SectionItem
                  title={
                    <>
                      {t('TEAM')}{' '}
                      <span>
                        {job.team_members_num
                          ? job.team_members_num + ' ' + t('PEOPLE')
                          : ''}
                      </span>
                    </>
                  }
                  name="team"
                  jobFeedbacks={job?.job_feedbacks}
                  trackSection={trackSection}
                  routeToEdit={isClient && `/edit/${job?.id}/team`}
                >
                  {teamGraph}
                </SectionItem>
              </Col>
            }
            {!!job?.activities.length &&
              <Col xs={{ span: 24 }} md={{ span: 24 }} xl={{ span: useThirdColumnLayout ? 8 : 12 }}>
                <SectionItem
                  title={t('WHAT_WILL_BE_DOING')}
                  name="will_be_doing"
                  jobFeedbacks={job?.job_feedbacks}
                  trackSection={trackSection}
                  routeToEdit={isClient && `/edit/${job?.id}/info`}
                >
                  <ActivitySection activities={job?.activities || []} />
                </SectionItem>
              </Col>
            }
            {!!job?.tools_used.length &&
              <Col xs={{ span: 24 }} md={{ span: 24 }} xl={{ span: useThirdColumnLayout ? 8 : 12 }}>
                <SectionItem
                  title={t('TOOLS_USED')}
                  name="tools_used"
                  jobFeedbacks={job?.job_feedbacks}
                  trackSection={trackSection}
                  routeToEdit={isClient && `/edit/${job?.id}/info`}
                >
                  {tollsGraph}
                </SectionItem>
              </Col>
            }
            {!!tree.length && (
              <Col
                xs={{ span: 24 }}
                md={{ span: 24 }}
                xl={{ span: job?.colleagues && job.colleagues.length ? 12 : 24 }}
              >
                <SectionItem
                  title={t('CAREER_PATH')}
                  dark={true}
                  name="career_path"
                  jobFeedbacks={job?.job_feedbacks}
                  trackSection={trackSection}
                  routeToEdit={isClient && `/edit/${job?.id}/offer`}
                >
                  <CareerSection career={tree} description={job?.growth_path} />
                </SectionItem>
              </Col>
            )}
            {!!job?.colleagues && job.colleagues.length > 0 && (
              <Col
                xs={{ span: 24 }}
                md={{ span: 24 }}
                xl={{ span: tree.length ? 12 : 24 }}
              >
                <SectionItem
                  title={t('COLLEAGUES')}
                  dark={true}
                  bg_image={<QuoteIcon className="section-bg" />}
                  name="colleagues"
                  jobFeedbacks={job?.job_feedbacks}
                  trackSection={trackSection}
                  routeToEdit={isClient && `/edit/${job?.id}/team`}
                >
                  <ColleagueSection
                    colleagues={job?.colleagues}
                    className={
                      !job?.job_feedbacks?.find(
                        (el) => el.tag_name === 'colleagues' && isRecruiter,
                      )
                        ? 'left'
                        : ''
                    }
                  />
                </SectionItem>
              </Col>
            )}
            <Col
              className={'application-process'}
              xs={{ span: 24 }}
              md={{ span: 24 }}
              xl={{ span: 24 }}
            >
              <SectionItem
                title={t('APPLICATION_PROCESS')}
                name="application_process"
                jobFeedbacks={job?.job_feedbacks}
                trackSection={trackSection}
                routeToEdit={isClient && `/edit/${job?.id}/process`}
              >
                <ApplicationProcessWrap>
                  {job?.interview_description ? (
                    <InterviewDescription>
                      {job?.interview_description}
                    </InterviewDescription>
                  ) : (
                    <ComingSoon
                      recruiterShortText={true}
                      onSetFavorite={setFavorite}
                      favorite={favorite}
                      job={job}
                    />
                  )}
                  {job?.stages && (
                    <ApplicationProcess
                      stages={job.stages}
                      duration={job.total_duration || 0}
                    />
                  )}
                </ApplicationProcessWrap>
              </SectionItem>
            </Col>
          </Row>
          {user?.role === RECRUITER_ROLE && <SimilarJobSection jobId={id} />}
          {inviteModal.showInviteAccept && (
            <StyledInviteModal
              visible={inviteModal.showInviteAccept}
              title={t('INVITE_MODAL_TITLE')}
              closable={false}
              footer={null}
              onCancel={() => {
                inviteModal.setShowInviteAccept(false);
                setStartIntroduction(false);
              }}
            >
              <InviteModalContent>
                <div style={{ marginBottom: 12, color: '#627d95', fontSize: 15 }}>{t('INVITE_MODAL_EXPLANATION')}</div>
                <StyledInviteInput
                  placeholder={t('INVITE_MODAL_FIRST_NAME')}
                  value={inviteModal.inviteName}
                  onChange={(e: any) => inviteModal.setInviteName(e.target.value)}
                />
                <StyledInviteInput
                  placeholder={t('INVITE_MODAL_LAST_NAME')}
                  value={inviteModal.inviteLastName}
                  onChange={(e: any) => inviteModal.setInviteLastName(e.target.value)}
                />
                <EmailInputGroup>
                  {(() => {
                    console.log('🔍 Modal email input logic:', {
                      domainValidationEnabled,
                      inviteDomainsLength: inviteDomains.length,
                      condition: domainValidationEnabled && inviteDomains.length > 0,
                      inviteDomain,
                      inviteDomains
                    });
                    return domainValidationEnabled && inviteDomains.length > 0;
                  })() ? (
                    <>
                      <EmailPrefixInput
                        placeholder={t('INVITE_MODAL_EMAIL')}
                        value={inviteEmailPrefix}
                        onChange={(e: any) => {
                          setInviteEmailPrefix(e.target.value);
                          if (inviteDomain) {
                            inviteModal.setInviteEmail(`${e.target.value}@${inviteDomain}`);
                          }
                        }}
                        disabled={!inviteDomain}
                      />
                      {inviteDomains.length > 1 ? (
                        <EmailDomainSelect
                          value={inviteDomain}
                          onChange={(val: any) => {
                            const domain = String(val);
                            setSelectedDomain(domain);
                            inviteModal.setInviteEmail(`${inviteEmailPrefix}@${domain}`);
                          }}
                          options={inviteDomains.map(d => ({ value: d, label: '@' + d }))}
                          suffixIcon={<DownOutlined style={{ color: '#627d95', fontSize: 14 }} />}
                        />
                      ) : (
                        <EmailDomainSuffix>@{inviteDomain || 'domain.com'}</EmailDomainSuffix>
                      )}
                    </>
                  ) : (
                    <StyledInviteInput
                      placeholder={t('INVITE_MODAL_EMAIL')}
                      value={inviteModal.inviteEmail}
                      onChange={(e: any) => inviteModal.setInviteEmail(e.target.value)}
                      type="email"
                    />
                  )}
                </EmailInputGroup>
                <StyledInviteButton
                  type="primary"
                  loading={inviteModal.inviteLoading}
                  onClick={() => {
                    if (domainValidationEnabled && inviteDomains.length > 0 && !inviteDomain) return;
                    inviteModal.handleInviteAccept();
                  }}
                  block
                >
                  {t('INVITE_MODAL_CONTINUE_BTN')}
                </StyledInviteButton>
              </InviteModalContent>
            </StyledInviteModal>
          )}
          {!inviteModal.showInviteAccept && startIntroduction && (isAuth || inviteModal.inviteAccepted) && (
            <>
              {user?.role !== CLIENT_ROLE && (
                <RoundAddButton onClick={() => setAddCandidate(true)} />
              )}
              <CandidateChooseModal
                candidate={selectedCandidate}
                {...supplementalJobData}
                jobId={id}
                recruiterPoints={job?.recruiter_points}
                introducedList={introducedList}
                roles={roles}
                show={startIntroduction}
                setShow={setStartIntroduction}
                setCandidate={setSelectedCandidate}
                addedCandidate={addedCandidate}
                inviteToken={inviteModal.inviteToken}
              />
            </>
          )}
          {selectedCandidate &&
            job &&
            (!job.company?.credit_system_enabled || (job.recruiter_points > 0 && job.is_active_recruiter_points !== false)) && (
              <CandidateIntroduceModal
                backButton={startIntroduction}
                jobId={id}
                job={job}
                addToList={setIntroducedList}
                candidate={selectedCandidate}
                setCandidate={setSelectedCandidate}
                skills={supplementalJobData.skills}
                certificates={supplementalJobData.certificates}
                education={supplementalJobData.education}
                languages={job?.languages ? job?.languages : []}
                languageList={languages}
                employmentType={employmentTypes.find(
                  (employmentType) =>
                    job.employment_type?.type.id === employmentType.id,
                )}
                onConfirmFinished={() => {
                  getJob(id).then((res) => {
                    setJob(res.data);
                  });
                }}
                inviteToken={inviteModal.inviteToken}
              />
            )}
          <CandidateAddForm
            cities={supplementalJobData.cities}
            certificates={supplementalJobData.certificates}
            education={supplementalJobData.education}
            onAction={editModal ? editCandidate : addCandidateItem}
            roles={roles}
            skills={supplementalJobData.skills}
            domains={supplementalJobData.domains}
            seniority={supplementalJobData.seniority}
            visible={addCandidate}
            changeVisibility={setAddCandidate}
            role={user?.role}
          />
        </SingleJobBody>
      </Loader>
    </div>
  );
};

export default SingleJob;